import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Wrench, Plus, X, Printer, MagnifyingGlass, PencilSimple,
  CheckCircle, Clock, Package, CheckSquare, XCircle, User,
} from "@phosphor-icons/react";
import RepairTicketPrint from "../components/RepairTicketPrint";

const fmt = (n) => new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2 }).format(n || 0);

const STATUS_CONFIG = {
  en_espera: { label: "En espera", cls: "bg-white/5 text-zinc-400 border-white/10", Icon: Clock },
  en_reparacion: { label: "En reparación", cls: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20", Icon: Wrench },
  lista_para_retirar: { label: "Lista para retirar", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20", Icon: Package },
  entregada: { label: "Entregada", cls: "bg-turquoise/10 text-turquoise border-turquoise/20", Icon: CheckCircle },
  rechazada: { label: "Rechazada", cls: "bg-red-500/10 text-red-400 border-red-500/20", Icon: XCircle },
};

const NEXT_STATUS = {
  en_espera: "en_reparacion",
  en_reparacion: "lista_para_retirar",
  lista_para_retirar: "entregada",
};

const EMPTY_FORM = {
  customer_id: null,
  customer_name: "",
  customer_phone: "",
  customer_document: "",
  device_brand: "",
  device_model: "",
  device_imei: "",
  device_color: "",
  device_password: "",
  device_accessories: "",
  device_condition: "",
  repair_description: "",
  private_notes: "",
  budget: "",
  deposit: "",
  estimated_date: "",
};

export default function Repairs() {
  const [repairs, setRepairs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [printRepair, setPrintRepair] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = useCallback(() => {
    api.get("/repairs").then(({ data }) => setRepairs(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = repairs.filter((r) => {
    if (filter !== "all" && r.status !== filter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      r.customer_name.toLowerCase().includes(q) ||
      String(r.order_number).includes(q) ||
      (r.device_imei || "").toLowerCase().includes(q) ||
      `${r.device_brand} ${r.device_model}`.toLowerCase().includes(q) ||
      (r.customer_phone || "").includes(q)
    );
  });

  const counts = repairs.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  const openRepair = async (repair_id) => {
    const { data } = await api.get(`/repairs/${repair_id}`);
    setSelected(data);
  };

  const refreshSelected = async () => {
    if (!selected) return;
    const { data } = await api.get(`/repairs/${selected.repair_id}`);
    setSelected(data);
    await load();
  };

  return (
    <div className="p-6 md:p-10" data-testid="repairs-page">
      <header className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-turquoise font-mono mb-1">Taller</p>
          <h1 className="text-3xl md:text-4xl font-heading font-bold">Reparaciones</h1>
          <p className="text-zinc-400 mt-1 text-sm">{repairs.length} órdenes registradas</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          data-testid="repairs-new-btn"
          className="bg-brand-gradient text-black font-semibold px-5 py-2.5 rounded-md flex items-center gap-2 shadow-cyan-glow hover:opacity-90 transition"
        >
          <Plus size={18} weight="bold" /> Nueva orden
        </button>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setFilter("all")}
          data-testid="repair-filter-all"
          className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider border transition ${filter === "all" ? "border-turquoise bg-turquoise/10 text-turquoise" : "border-white/10 text-zinc-400 hover:border-white/20"}`}
        >
          Todas ({repairs.length})
        </button>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            data-testid={`repair-filter-${key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider border transition ${filter === key ? cfg.cls + " opacity-100" : "border-white/10 text-zinc-400 hover:border-white/20"}`}
          >
            {cfg.label} ({counts[key] || 0})
          </button>
        ))}
      </div>

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Buscar por orden, cliente, equipo, IMEI, teléfono..."
        data-testid="repairs-search-input"
        className="w-full max-w-md bg-[#0F1115] border border-white/10 rounded-md px-4 py-2 text-sm mb-4 font-mono"
      />

      {/* List */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/5">
              <tr>
                <th className="text-left p-4 font-mono">Orden</th>
                <th className="text-left p-4 font-mono">Ingreso</th>
                <th className="text-left p-4 font-mono">Cliente</th>
                <th className="text-left p-4 font-mono">Equipo</th>
                <th className="text-left p-4 font-mono">Reparación</th>
                <th className="text-left p-4 font-mono">Estado</th>
                <th className="text-right p-4 font-mono">Saldo</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="8" className="p-12 text-center text-zinc-500">Sin órdenes</td></tr>
              ) : filtered.map((r) => {
                const st = STATUS_CONFIG[r.status] || STATUS_CONFIG.en_espera;
                return (
                  <tr
                    key={r.repair_id}
                    className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                    onClick={() => openRepair(r.repair_id)}
                    data-testid={`repair-row-${r.repair_id}`}
                  >
                    <td className="p-4 font-mono text-turquoise font-bold">#{r.order_number}</td>
                    <td className="p-4 text-xs font-mono text-zinc-400">{new Date(r.created_at).toLocaleDateString("es-AR")}</td>
                    <td className="p-4 font-medium">{r.customer_name}</td>
                    <td className="p-4 text-zinc-300">{r.device_brand} {r.device_model}</td>
                    <td className="p-4 text-xs text-zinc-400 max-w-[220px] truncate">{r.repair_description}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-[10px] uppercase font-mono tracking-wider border ${st.cls}`}>{st.label}</span>
                    </td>
                    <td className={`p-4 text-right font-mono ${r.balance > 0 ? "text-amber-400" : "text-turquoise"}`}>
                      $ {fmt(r.balance)}
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); setPrintRepair(r); }}
                        className="text-zinc-400 hover:text-turquoise"
                        data-testid={`repair-print-row-${r.repair_id}`}
                        title="Imprimir orden"
                      >
                        <Printer size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {createOpen && (
        <CreateRepairModal
          onClose={() => setCreateOpen(false)}
          onSuccess={(created) => {
            setCreateOpen(false);
            load();
            setPrintRepair(created);
          }}
        />
      )}

      {selected && (
        <RepairDetailModal
          repair={selected}
          isAdmin={isAdmin}
          onClose={() => setSelected(null)}
          onRefresh={refreshSelected}
          onPrint={() => setPrintRepair(selected)}
        />
      )}

      {printRepair && <RepairTicketPrint repair={printRepair} onClose={() => setPrintRepair(null)} />}
    </div>
  );
}

function CreateRepairModal({ onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [customerMode, setCustomerMode] = useState("casual");
  const [customers, setCustomers] = useState([]);
  const [custQuery, setCustQuery] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const filteredCustomers = customers.filter((c) =>
    !custQuery || c.name.toLowerCase().includes(custQuery.toLowerCase()) || (c.phone || "").includes(custQuery)
  ).slice(0, 5);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) return toast.error("Ingresá el nombre del cliente");
    if (!form.repair_description.trim()) return toast.error("Describí la reparación a realizar");
    setLoading(true);
    try {
      const { data } = await api.post("/repairs", {
        ...form,
        customer_id: customerMode === "registered" ? form.customer_id : null,
        budget: parseFloat(form.budget) || 0,
        deposit: parseFloat(form.deposit) || 0,
      });
      toast.success(`Orden #${data.order_number} creada`);
      onSuccess(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear la orden");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="repair-create-modal">
      <form onSubmit={submit} className="glass-card rounded-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-semibold flex items-center gap-2">
            <Wrench size={22} weight="duotone" className="text-turquoise" /> Nueva orden de reparación
          </h3>
          <button type="button" onClick={onClose} data-testid="repair-create-close"><X size={20} /></button>
        </div>

        {/* Customer mode */}
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">Cliente</p>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              type="button"
              onClick={() => { setCustomerMode("casual"); set("customer_id", null); }}
              data-testid="repair-customer-casual"
              className={`py-2 rounded-md border text-xs font-mono uppercase tracking-wider transition ${customerMode === "casual" ? "border-turquoise bg-turquoise/10 text-turquoise" : "border-white/10 text-zinc-400"}`}
            >
              Cliente ocasional
            </button>
            <button
              type="button"
              onClick={() => setCustomerMode("registered")}
              data-testid="repair-customer-registered"
              className={`py-2 rounded-md border text-xs font-mono uppercase tracking-wider transition ${customerMode === "registered" ? "border-turquoise bg-turquoise/10 text-turquoise" : "border-white/10 text-zinc-400"}`}
            >
              Cliente registrado
            </button>
          </div>

          {customerMode === "casual" ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input placeholder="Nombre *" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="repair-cust-name" className="bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm" />
              <input placeholder="Teléfono" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} data-testid="repair-cust-phone" className="bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm" />
              <input placeholder="DNI (opcional)" value={form.customer_document} onChange={(e) => set("customer_document", e.target.value)} data-testid="repair-cust-doc" className="bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm" />
            </div>
          ) : (
            <div className="relative">
              <input
                placeholder="Buscar cliente registrado..."
                value={custQuery}
                onChange={(e) => setCustQuery(e.target.value)}
                data-testid="repair-cust-search"
                className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm"
              />
              {form.customer_id ? (
                <div className="mt-2 flex items-center justify-between bg-turquoise/5 border border-turquoise/20 rounded-md px-3 py-2 text-sm">
                  <span className="flex items-center gap-2"><User size={14} className="text-turquoise" /> {form.customer_name} — {form.customer_phone}</span>
                  <button type="button" onClick={() => { set("customer_id", null); set("customer_name", ""); set("customer_phone", ""); }} data-testid="repair-cust-clear"><X size={14} /></button>
                </div>
              ) : custQuery && (
                <div className="absolute z-10 left-0 right-0 mt-1 glass-card rounded-md border border-white/10 max-h-44 overflow-y-auto">
                  {filteredCustomers.length === 0 ? (
                    <p className="p-3 text-xs text-zinc-500">Sin resultados</p>
                  ) : filteredCustomers.map((c) => (
                    <button
                      type="button"
                      key={c.customer_id}
                      onClick={() => { set("customer_id", c.customer_id); set("customer_name", c.name); set("customer_phone", c.phone || ""); set("customer_document", c.document || ""); setCustQuery(""); }}
                      data-testid={`repair-cust-opt-${c.customer_id}`}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex justify-between"
                    >
                      <span>{c.name}</span>
                      <span className="text-zinc-500 text-xs font-mono">{c.phone}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Device */}
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">Datos del equipo</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <input placeholder="Marca *" value={form.device_brand} onChange={(e) => set("device_brand", e.target.value)} data-testid="repair-dev-brand" className="bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm" />
          <input placeholder="Modelo *" value={form.device_model} onChange={(e) => set("device_model", e.target.value)} data-testid="repair-dev-model" className="bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm" />
          <input placeholder="Color" value={form.device_color} onChange={(e) => set("device_color", e.target.value)} data-testid="repair-dev-color" className="bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm" />
          <input placeholder="IMEI / N° serie" value={form.device_imei} onChange={(e) => set("device_imei", e.target.value)} data-testid="repair-dev-imei" className="bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
          <input placeholder="Contraseña / patrón" value={form.device_password} onChange={(e) => set("device_password", e.target.value)} data-testid="repair-dev-pass" className="bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm" />
          <input placeholder="Accesorios entregados" value={form.device_accessories} onChange={(e) => set("device_accessories", e.target.value)} data-testid="repair-dev-acc" className="bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm" />
        </div>
        <input placeholder="Estado estético (rayones, roturas, pantalla...)" value={form.device_condition} onChange={(e) => set("device_condition", e.target.value)} data-testid="repair-dev-cond" className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm mb-4" />

        {/* Repair */}
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">Reparación</p>
        <textarea
          placeholder="Descripción de la reparación a realizar *"
          value={form.repair_description}
          onChange={(e) => set("repair_description", e.target.value)}
          rows={2}
          data-testid="repair-description"
          className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm mb-3 resize-none"
        />
        <textarea
          placeholder="Notas internas (solo visibles para el técnico)"
          value={form.private_notes}
          onChange={(e) => set("private_notes", e.target.value)}
          rows={2}
          data-testid="repair-private-notes"
          className="w-full bg-white/[0.03] border border-amber-500/20 rounded-md px-3 py-2 text-sm mb-4 resize-none"
        />

        {/* Money */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1 block">Presupuesto $</label>
            <input type="number" min="0" value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="repair-budget" className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1 block">Seña $</label>
            <input type="number" min="0" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} data-testid="repair-deposit" className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1 block">Entrega estimada</label>
            <input type="date" value={form.estimated_date} onChange={(e) => set("estimated_date", e.target.value)} data-testid="repair-est-date" className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm" />
          </div>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={onClose} data-testid="repair-create-cancel" className="flex-1 py-2.5 rounded-md border border-white/10 text-sm font-medium hover:bg-white/5">
            Cancelar
          </button>
          <button type="submit" disabled={loading} data-testid="repair-create-submit" className="flex-1 py-2.5 rounded-md text-sm font-semibold text-black bg-brand-gradient hover:opacity-90 disabled:opacity-40 transition">
            {loading ? "Creando..." : "Crear orden e imprimir"}
          </button>
        </div>
      </form>
    </div>
  );
}

function RepairDetailModal({ repair, isAdmin, onClose, onRefresh, onPrint }) {
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const st = STATUS_CONFIG[repair.status] || STATUS_CONFIG.en_espera;
  const nextStatus = NEXT_STATUS[repair.status];
  const canReject = ["en_espera", "en_reparacion"].includes(repair.status);

  const changeStatus = async (status) => {
    setLoading(true);
    try {
      await api.post(`/repairs/${repair.repair_id}/status`, { status, note });
      toast.success(`Estado: ${STATUS_CONFIG[status].label}`);
      setNote("");
      await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar estado");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="repair-detail-modal">
      <div className="glass-card rounded-xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Wrench size={20} className="text-turquoise" /> Orden #{repair.order_number}
          </h3>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-[10px] uppercase font-mono tracking-wider border ${st.cls}`}>{st.label}</span>
            <button onClick={onClose} data-testid="repair-detail-close"><X size={20} /></button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-4">
          <Row2 l="Cliente" v={repair.customer_name} />
          <Row2 l="Tel" v={repair.customer_phone || "—"} />
          <Row2 l="Equipo" v={`${repair.device_brand} ${repair.device_model}`} />
          <Row2 l="Color" v={repair.device_color || "—"} />
          <Row2 l="IMEI" v={repair.device_imei || "—"} mono />
          <Row2 l="Clave" v={repair.device_password || "SIN CLAVE"} mono />
          <Row2 l="Accesorios" v={repair.device_accessories || "—"} />
          <Row2 l="Estado estético" v={repair.device_condition || "—"} />
        </div>

        <div className="border-y border-white/5 py-3 mb-4">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1">Reparación</p>
          <p className="text-sm">{repair.repair_description}</p>
          {repair.private_notes && (
            <>
              <p className="text-[10px] uppercase tracking-wider text-amber-500 font-mono mt-3 mb-1">Notas internas</p>
              <p className="text-sm text-amber-200/80">{repair.private_notes}</p>
            </>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 text-sm font-mono mb-4">
          <div className="bg-white/[0.03] rounded-md p-2 text-center">
            <p className="text-[10px] text-zinc-500 uppercase">Presupuesto</p>
            <p className="text-white">$ {fmt(repair.budget)}</p>
          </div>
          <div className="bg-white/[0.03] rounded-md p-2 text-center">
            <p className="text-[10px] text-zinc-500 uppercase">Seña</p>
            <p className="text-turquoise">$ {fmt(repair.deposit)}</p>
          </div>
          <div className="bg-white/[0.03] rounded-md p-2 text-center">
            <p className="text-[10px] text-zinc-500 uppercase">Saldo</p>
            <p className="text-amber-400">$ {fmt(repair.balance)}</p>
          </div>
        </div>

        {repair.warranty_until && (
          <div className="mb-4 bg-turquoise/5 border border-turquoise/20 rounded-md p-3 text-xs font-mono">
            <span className="text-turquoise">Garantía de 30 días — válida hasta: {new Date(repair.warranty_until + "T12:00:00").toLocaleDateString("es-AR")}</span>
          </div>
        )}

        {/* History */}
        {repair.history && repair.history.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">Historial</p>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {repair.history.map((h, i) => (
                <div key={i} className="text-xs font-mono flex gap-2 items-baseline">
                  <span className="text-zinc-500 whitespace-nowrap">{new Date(h.at).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                  <span className="text-turquoise">{STATUS_CONFIG[h.action]?.label || h.action}</span>
                  <span className="text-zinc-400 truncate">— {h.by}{h.note ? `: ${h.note}` : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        {repair.status !== "entregada" && repair.status !== "rechazada" && (
          <div className="mb-4">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Nota del cambio de estado (opcional)"
              data-testid="repair-status-note"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm mb-2"
            />
            <div className="flex gap-2 flex-wrap">
              {nextStatus && (
                <button
                  onClick={() => changeStatus(nextStatus)}
                  disabled={loading}
                  data-testid="repair-status-next"
                  className="flex-1 min-w-[140px] py-2.5 rounded-md text-sm font-semibold text-black bg-brand-gradient hover:opacity-90 disabled:opacity-40 transition flex items-center justify-center gap-2"
                >
                  <CheckSquare size={16} weight="bold" />
                  Marcar: {STATUS_CONFIG[nextStatus].label}
                </button>
              )}
              {canReject && (
                <button
                  onClick={() => changeStatus("rechazada")}
                  disabled={loading}
                  data-testid="repair-status-reject"
                  className="px-4 py-2.5 rounded-md text-sm font-semibold border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-40 transition"
                >
                  Rechazada
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => setEditOpen(true)}
                  data-testid="repair-edit-btn"
                  className="px-4 py-2.5 rounded-md text-sm font-semibold border border-white/15 hover:bg-white/5 transition flex items-center gap-2"
                >
                  <PencilSimple size={14} /> Editar
                </button>
              )}
            </div>
          </div>
        )}

        <button
          onClick={onPrint}
          data-testid="repair-detail-print-btn"
          className="w-full py-2.5 rounded-md text-sm font-semibold border border-turquoise/40 text-turquoise hover:bg-turquoise/10 transition flex items-center justify-center gap-2"
        >
          <Printer size={16} weight="bold" /> Imprimir orden (2 copias)
        </button>

        {editOpen && (
          <EditRepairModal
            repair={repair}
            onClose={() => setEditOpen(false)}
            onSuccess={async () => { setEditOpen(false); await onRefresh(); }}
          />
        )}
      </div>
    </div>
  );
}

function Row2({ l, v, mono }) {
  return (
    <div className="flex justify-between gap-2 py-0.5">
      <span className="text-zinc-500 text-xs">{l}</span>
      <span className={`text-xs text-right ${mono ? "font-mono" : ""}`}>{v}</span>
    </div>
  );
}

function EditRepairModal({ repair, onClose, onSuccess }) {
  const [form, setForm] = useState({
    device_password: repair.device_password || "",
    device_accessories: repair.device_accessories || "",
    device_condition: repair.device_condition || "",
    private_notes: repair.private_notes || "",
    budget: repair.budget,
    deposit: repair.deposit,
    estimated_date: repair.estimated_date || "",
    technician_name: repair.technician_name || "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch(`/repairs/${repair.repair_id}`, {
        ...form,
        budget: parseFloat(form.budget) || 0,
        deposit: parseFloat(form.deposit) || 0,
      });
      toast.success("Orden actualizada");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="repair-edit-modal">
      <form onSubmit={submit} className="glass-card rounded-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold">Editar orden #{repair.order_number}</h3>
          <button type="button" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <input placeholder="Contraseña" value={form.device_password} onChange={(e) => set("device_password", e.target.value)} className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm" />
          <input placeholder="Accesorios" value={form.device_accessories} onChange={(e) => set("device_accessories", e.target.value)} className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm" />
          <input placeholder="Estado estético" value={form.device_condition} onChange={(e) => set("device_condition", e.target.value)} className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm" />
          <textarea placeholder="Notas internas" value={form.private_notes} onChange={(e) => set("private_notes", e.target.value)} rows={2} className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm resize-none" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1 block">Presupuesto $</label>
              <input type="number" min="0" value={form.budget} onChange={(e) => set("budget", e.target.value)} className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1 block">Seña $</label>
              <input type="number" min="0" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1 block">Entrega estimada</label>
            <input type="date" value={form.estimated_date} onChange={(e) => set("estimated_date", e.target.value)} className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1 block">Técnico asignado</label>
            <input value={form.technician_name} onChange={(e) => set("technician_name", e.target.value)} className="w-full bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-md border border-white/10 text-sm font-medium hover:bg-white/5">Cancelar</button>
          <button type="submit" disabled={loading} data-testid="repair-edit-submit" className="flex-1 py-2.5 rounded-md text-sm font-semibold text-black bg-brand-gradient hover:opacity-90 disabled:opacity-40">
            {loading ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}
